import {
  Loader2Icon,
  Link2,
  CloudUpload,
  X,
  AlertTriangle,
  Table,
} from 'lucide-react'
import { useTranslations } from 'next-intl'
import { toast } from 'sonner'
import { useCallback, useRef, useState, useEffect } from 'react'
import {
  ALLOWED_UPLOAD_ASSETS_FORMATS,
  useUploadAssets,
} from '@/hooks/use-upload-assets'
import type { Asset, AssetSource } from '@/lib/types'
import { pushModal, popModal } from '@/components/modals'

const MAX_LOGO_ASSETS = 1
export const MAX_CONTENT_ASSETS = 5
const MAX_TABLE_ASSETS = 1

export function UploadAssetsModal({
  onSave,
  onClose,
  aiQuestions = '',
  payload,
  isDropdownMode = false,
  existingContentCount = 0,
}: {
  onSave: (payload: any) => void
  onClose: () => void
  aiQuestions?: string
  payload: {
    logoAssets: Asset[]
    contentAssets: Asset[]
  }
  isDropdownMode?: boolean
  existingContentCount?: number
}) {
  const logoFileInputRef = useRef<HTMLInputElement>(null)
  const contentFileInputRef = useRef<HTMLInputElement>(null)

  const [logoAssets, setLogoAssets] = useState<Asset[]>(
    payload.logoAssets || [],
  )
  const [contentAssets, setContentAssets] = useState<Asset[]>(
    payload.contentAssets || [],
  )
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const t = useTranslations('modals.upload-assets')
  const tableT = useTranslations('modals.table-upload')

  // Simple mobile detection hook
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    // Check on mount
    checkMobile()

    // Add resize listener
    window.addEventListener('resize', checkMobile)

    return () => {
      window.removeEventListener('resize', checkMobile)
    }
  }, [])

  // Helper function to count table assets (type 'table')
  const getTableAssetsCount = () => {
    return contentAssets.filter((asset) => asset.type === 'table').length
  }

  // Helper function to count non-table content assets
  const getNonTableContentAssetsCount = () => {
    return contentAssets.filter((asset) => asset.type !== 'table').length
  }

  const logoUpload = useUploadAssets({ unlimited: true })
  const contentUpload = useUploadAssets({ unlimited: true })

  // Auto-dismiss error after 5 seconds
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage(null)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [errorMessage])

  const handleUploadLogo = useCallback(
    async (e: any) => {
      const files = Array.from(e.target.files || []) as File[]

      // Check upload limits with detailed error messages for logo
      const currentCount = logoAssets.length
      const selectedCount = files.length
      const totalAfterUpload = currentCount + selectedCount

      if (totalAfterUpload > MAX_LOGO_ASSETS) {
        const remaining = Math.max(0, MAX_LOGO_ASSETS - currentCount)
        setErrorMessage(t('maximum_logo_error', { MAX_LOGO_ASSETS }))
        return
      }
      if (files.length === 0) return

      // Create temporary assets immediately to show files with shimmer
      const tempAssets: Asset[] = files.map((file) => {
        const tempId = `temp-${Date.now()}-${Math.random()}`
        const isImage = file.type.startsWith('image/')
        const thumbnail = isImage ? URL.createObjectURL(file) : undefined

        let assetType: Asset['type'] = 'image'
        if (file.type.startsWith('video/')) assetType = 'video'
        else if (file.type.startsWith('audio/')) assetType = 'audio'
        else if (file.type.startsWith('application/pdf')) assetType = 'pdf'
        else if (file.type.startsWith('text/csv')) assetType = 'csv'

        return {
          source: 'upload' as const,
          type: assetType,
          link: `temp://${tempId}`,
          thumbnail,
          name: 'This is the project logo',
          payload: { tempId, isUploading: true },
        }
      })

      // Add temporary assets immediately (user sees them right away with shimmer)
      const tempLogoAssets = [...logoAssets, ...tempAssets]
      setLogoAssets(tempLogoAssets)

      // Auto save and close in dropdown mode (with temp assets)
      if (isDropdownMode) {
        onSave({
          logoAssets: tempLogoAssets,
          contentAssets,
        })
        onClose()
      }

      try {
        // Upload files
        const result = await logoUpload.uploadAssets(files, {
          assetSource: 'chat',
          assetDir: 'chat-images',
        })

        if (!result.success) {
          toast.error(result.error, { duration: 2000 })
          return
        }

        const newLogoAssets = [
          ...logoAssets,
          ...result.assets.map((item) => {
            return {
              ...item,
              source: 'upload' as AssetSource,
              name: 'This is the project logo',
            }
          }),
        ]

        // Replace temporary assets with real ones
        setLogoAssets(newLogoAssets)

        // Clean up object URLs
        tempAssets.forEach((asset) => {
          if (asset.thumbnail && asset.thumbnail.startsWith('blob:')) {
            URL.revokeObjectURL(asset.thumbnail)
          }
        })

        // Auto save and close in dropdown mode (with real assets)
        if (isDropdownMode) {
          onSave({
            logoAssets: newLogoAssets,
            contentAssets,
          })
          onClose()
        }
      } catch (error) {
        // Remove temporary assets on error
        setLogoAssets(logoAssets)

        // Clean up object URLs on error
        tempAssets.forEach((asset) => {
          if (asset.thumbnail && asset.thumbnail.startsWith('blob:')) {
            URL.revokeObjectURL(asset.thumbnail)
          }
        })
      }
    },
    [
      logoUpload.uploadAssets,
      logoAssets,
      t,
      isDropdownMode,
      onSave,
      contentAssets,
      onClose,
    ],
  )

  const handleTableUpload = useCallback(
    (
      config: {
        ui_table_name: string
        db_table_name: string
        field_list: any[]
      },
      rows: string[][],
      mongoId?: string,
    ) => {
      // Generate SQL migration from the config field_list
      const headers = config.field_list.map((field) => field.ui_name)
      const columns = config.field_list.map((field) => ({ type: field.type }))

      // Simple SQL migration generation
      const generateSQLMigration = (
        headers: string[],
        columns: any[],
        rows: string[][],
      ) => {
        if (!headers || headers.length === 0) return ''

        const columnDefinitions = headers
          .map((header, index) => {
            const column = columns?.[index]
            let sqlType = 'TEXT'

            if (column?.type) {
              switch (column.type) {
                case 'number':
                  sqlType = 'INTEGER'
                  break
                case 'boolean':
                  sqlType = 'BOOLEAN'
                  break
                case 'date':
                  sqlType = 'DATE'
                  break
                case 'email':
                  sqlType = 'VARCHAR(255)'
                  break
                default:
                  sqlType = 'TEXT'
              }
            }

            return `"${header}" ${sqlType}`
          })
          .join(',\n  ')

        // Generate INSERT statements for all rows
        const insertStatements = rows
          .map((row, index) => {
            const values = row
              .map((value, colIndex) => {
                const column = columns?.[colIndex]
                if (column?.type === 'number') {
                  // Handle numeric values
                  const numValue = parseFloat(value)
                  return isNaN(numValue) ? 'NULL' : numValue.toString()
                } else if (column?.type === 'boolean') {
                  // Handle boolean values
                  if (value === 'true' || value === '1') return 'true'
                  if (value === 'false' || value === '0') return 'false'
                  return 'NULL'
                } else if (
                  value === '' ||
                  value === null ||
                  value === undefined
                ) {
                  return 'NULL'
                } else {
                  // Handle text values - escape single quotes
                  const escapedValue = value.toString().replace(/'/g, "''")
                  return `'${escapedValue}'`
                }
              })
              .join(', ')

            return `INSERT INTO user_table (${headers.map((h) => `"${h}"`).join(', ')}) VALUES (${values});`
          })
          .join('\n')

        return `CREATE TABLE user_table (
   id SERIAL PRIMARY KEY,
   ${columnDefinitions}
 );

 ${insertStatements}`
      }

      const sqlMigration = generateSQLMigration(headers, columns, rows)

      // Convert config to a structured format that can be used as an asset
      const tableAsset: Asset = {
        link: `table-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, // Unique identifier for table assets
        type: 'table' as const, // Use 'table' type instead of 'csv'
        source: 'upload' as AssetSource,
        name: config.ui_table_name || 'User Table',
        payload: {
          config: config, // Store the config object as provided
          migration: sqlMigration,
        },
      }

      // Add new asset (editing is handled by passing loadTableId to modal)
      const newContentAssets = [...contentAssets, tableAsset]

      setContentAssets(newContentAssets)

      if (isDropdownMode) {
        onSave({
          logoAssets,
          contentAssets: newContentAssets,
        })
        onClose()
      }

      popModal()
    },
    [contentAssets, isDropdownMode, onSave, logoAssets, onClose],
  )

  const handleUploadContentAssets = useCallback(
    async (e: any) => {
      const files = Array.from(e.target.files || []) as File[]

      // Check upload limits with detailed error messages for content assets (excluding tables)
      const currentCount =
        getNonTableContentAssetsCount() + existingContentCount
      const selectedCount = files.length
      const totalAfterUpload = currentCount + selectedCount

      if (totalAfterUpload > MAX_CONTENT_ASSETS) {
        const remaining = Math.max(0, MAX_CONTENT_ASSETS - currentCount)
        setErrorMessage(
          t('maximum_content_error', { MAX_CONTENT_ASSETS, remaining }),
        )
        return
      }

      if (files.length === 0) return

      // Create temporary assets immediately to show files with shimmer
      const tempAssets: Asset[] = files.map((file) => {
        const tempId = `temp-${Date.now()}-${Math.random()}`
        const isImage = file.type.startsWith('image/')
        const thumbnail = isImage ? URL.createObjectURL(file) : undefined

        let assetType: Asset['type'] = 'image'
        if (file.type.startsWith('video/')) assetType = 'video'
        else if (file.type.startsWith('audio/')) assetType = 'audio'
        else if (file.type.startsWith('application/pdf')) assetType = 'pdf'
        else if (file.type.startsWith('text/csv')) assetType = 'csv'

        return {
          source: 'upload' as const,
          type: assetType,
          link: `temp://${tempId}`,
          thumbnail,
          name: file.name,
          payload: { tempId, isUploading: true },
        }
      })

      // Add temporary assets immediately (user sees them right away with shimmer)
      const tempContentAssets = [...contentAssets, ...tempAssets]
      setContentAssets(tempContentAssets)

      // Auto save and close in dropdown mode (with temp assets)
      if (isDropdownMode) {
        onSave({
          logoAssets,
          contentAssets: tempContentAssets,
        })
        onClose()
      }

      try {
        // Upload files
        const result = await contentUpload.uploadAssets(files, {
          assetSource: 'chat',
          assetDir: 'chat-images',
        })

        if (!result.success) {
          toast.error(result.error, { duration: 2000 })
          return
        }

        const newContentAssets = [
          ...contentAssets,
          ...result.assets.map((item) => {
            return {
              ...item,
              source: 'upload' as AssetSource,
            }
          }),
        ]

        // Replace temporary assets with real ones
        setContentAssets(newContentAssets)

        // Clean up object URLs
        tempAssets.forEach((asset) => {
          if (asset.thumbnail && asset.thumbnail.startsWith('blob:')) {
            URL.revokeObjectURL(asset.thumbnail)
          }
        })

        // Auto save and close in dropdown mode (with real assets)
        if (isDropdownMode) {
          onSave({
            logoAssets,
            contentAssets: newContentAssets,
          })
          onClose()
        }
      } catch (error) {
        // Remove temporary assets on error
        setContentAssets(contentAssets)

        // Clean up object URLs on error
        tempAssets.forEach((asset) => {
          if (asset.thumbnail && asset.thumbnail.startsWith('blob:')) {
            URL.revokeObjectURL(asset.thumbnail)
          }
        })
      }
    },
    [
      contentUpload.uploadAssets,
      contentAssets,
      t,
      isDropdownMode,
      onSave,
      logoAssets,
      onClose,
    ],
  )

  // Render dropdown-style content if in dropdown mode
  if (isDropdownMode) {
    console.log('contentAssets state:', contentAssets)
    return (
      <>
        {/* Error Banner */}
        {errorMessage && (
          <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[9999] animate-in slide-in-from-top duration-300">
            <div className="bg-[#332a1e] backdrop-blur-lg rounded-xl px-4 py-3 shadow-lg border border-white/10 max-w-md mx-4 relative">
              <button
                onClick={() => setErrorMessage(null)}
                className="absolute top-2 right-2 text-white/80 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-3 pr-6">
                <AlertTriangle className="w-5 h-5 text-white flex-shrink-0" />
                <div>
                  <p className="text-white font-light text-sm whitespace-nowrap">
                    {errorMessage}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="w-fit min-w-40 max-w-80 bg-[#332a1e] backdrop-blur-lg rounded-xl p-1 shadow-2xl border border-white/10">
          {/* Logo Upload Button with Tooltip */}
          <div className="relative group">
            <div
              className={`w-full flex items-center gap-2 p-2 rounded-lg transition-all duration-200 text-white border-none mb-0.5 ${
                logoUpload.isUploading || logoAssets.length >= MAX_LOGO_ASSETS
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-white/10 cursor-pointer'
              }`}
              onClick={() => {
                if (
                  !(
                    logoUpload.isUploading ||
                    logoAssets.length >= MAX_LOGO_ASSETS
                  )
                ) {
                  logoFileInputRef.current?.click()
                }
              }}
            >
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                {logoUpload.isUploading ? (
                  <Loader2Icon className="w-3 h-3 text-white opacity-80 animate-spin" />
                ) : (
                  <CloudUpload className="w-3 h-3 text-white opacity-80" />
                )}
              </div>
              <div className="text-left flex-1">
                <div className="font-light text-sm text-white opacity-80 whitespace-nowrap">
                  {t('upload_logo_title')}
                </div>
              </div>
              <input
                ref={logoFileInputRef}
                type="file"
                className="hidden"
                accept={'.jpg,.png'}
                multiple
                disabled={
                  logoUpload.isUploading || logoAssets.length >= MAX_LOGO_ASSETS
                }
                onChange={handleUploadLogo}
              />
            </div>

            {/* Tooltip for logo button */}
            {logoAssets.length >= MAX_LOGO_ASSETS ? (
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block pointer-events-none z-50">
                <div className="px-2 py-1 bg-black/80 backdrop-blur-md text-white text-xs rounded whitespace-nowrap">
                  {t('logo_upload_limit_error', { MAX_LOGO_ASSETS })}
                </div>
                <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black/50"></div>
              </div>
            ) : (
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block pointer-events-none z-50 tooltip-delay">
                <div className="px-2 py-1 bg-black/80 backdrop-blur-md text-white text-xs rounded whitespace-nowrap">
                  {t('logo_tooltip_text', { MAX_LOGO_ASSETS })}
                </div>
                <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black/50"></div>
              </div>
            )}
          </div>

          {/* Content Upload Button with Tooltip */}
          <div className="relative group">
            <div
              className={`w-full flex items-center gap-2 p-2 rounded-lg transition-all duration-200 text-white border-none mb-0.5 ${
                contentUpload.isUploading ||
                getNonTableContentAssetsCount() + existingContentCount >=
                  MAX_CONTENT_ASSETS
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-white/10 cursor-pointer'
              }`}
              onClick={() => {
                if (
                  !(
                    contentUpload.isUploading ||
                    getNonTableContentAssetsCount() + existingContentCount >=
                      MAX_CONTENT_ASSETS
                  )
                ) {
                  contentFileInputRef.current?.click()
                }
              }}
            >
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                {contentUpload.isUploading ? (
                  <Loader2Icon className="w-3 h-3 text-white opacity-80 animate-spin" />
                ) : (
                  <Link2 className="w-3 h-3 text-white opacity-80 rotate-45" />
                )}
              </div>
              <div className="text-left flex-1">
                <div className="font-light text-sm text-white opacity-80 whitespace-nowrap">
                  {t('upload_content_title')}
                </div>
              </div>
              <input
                ref={contentFileInputRef}
                type="file"
                className="hidden"
                accept={ALLOWED_UPLOAD_ASSETS_FORMATS.join(',')}
                multiple
                onChange={handleUploadContentAssets}
                disabled={
                  contentUpload.isUploading ||
                  getNonTableContentAssetsCount() + existingContentCount >=
                    MAX_CONTENT_ASSETS
                }
              />
            </div>

            {/* Tooltip for content button */}
            {getNonTableContentAssetsCount() + existingContentCount >=
            MAX_CONTENT_ASSETS ? (
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block pointer-events-none z-50">
                <div className="px-2 py-1  bg-black/80 backdrop-blur-md text-white text-xs rounded whitespace-nowrap opacity-95">
                  {t('content_upload_limit_error', { MAX_CONTENT_ASSETS })}
                </div>
                <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black/50 opacity-95"></div>
              </div>
            ) : (
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block pointer-events-none z-50 tooltip-delay">
                <div className="px-2 py-1  bg-black/80 backdrop-blur-md text-white text-xs rounded whitespace-nowrap opacity-95">
                  {t('content_tooltip_text', { MAX_CONTENT_ASSETS })}
                </div>
                <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black/50 opacity-95"></div>
              </div>
            )}
          </div>

          {/* Add Table Button with Tooltip */}
          <div className="relative group">
            <div
              className={`w-full flex items-center gap-2 p-2 rounded-lg transition-all duration-200 text-white border-none ${
                isMobile || getTableAssetsCount() >= MAX_TABLE_ASSETS
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-white/10 cursor-pointer'
              }`}
              onClick={() => {
                if (!isMobile && getTableAssetsCount() < MAX_TABLE_ASSETS) {
                  pushModal('TableUploadModal', {
                    onSave: handleTableUpload,
                    onClose: () => popModal(),
                  })
                }
              }}
            >
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <Table className="w-3 h-3 text-white opacity-80" />
              </div>
              <div className="text-left flex-1">
                <div className="font-light text-sm text-white opacity-80 whitespace-nowrap">
                  {t('add_table')}
                </div>
              </div>
            </div>

            {/* Tooltip for table button */}
            {isMobile ? (
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block pointer-events-none z-50">
                <div className="px-2 py-1 bg-black/80 backdrop-blur-md text-white text-xs rounded whitespace-nowrap opacity-95">
                  {t('table_not_available_mobile')}
                </div>
                <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black/50 opacity-95"></div>
              </div>
            ) : getTableAssetsCount() >= MAX_TABLE_ASSETS ? (
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block pointer-events-none z-50">
                <div className="px-2 py-1 bg-black/80 backdrop-blur-md text-white text-xs rounded whitespace-nowrap opacity-95">
                  Table limit reached (Max {MAX_TABLE_ASSETS})
                </div>
                <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black/50 opacity-95"></div>
              </div>
            ) : (
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block pointer-events-none z-50 tooltip-delay">
                <div className="px-2 py-1 bg-black/80 backdrop-blur-md text-white text-xs rounded whitespace-nowrap opacity-95">
                  Upload CSV/Excel file to create table (Max {MAX_TABLE_ASSETS})
                </div>
                <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black/50 opacity-95"></div>
              </div>
            )}
          </div>
        </div>
      </>
    )
  }
}
